import { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { ISerologyBloodDraw } from '@nkr/models/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ColDef, FirstDataRenderedEvent, GridApi, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: ISerologyBloodDraw[],
    onViewResults: (row: ISerologyBloodDraw) => void
}

const Grid = (props: IProps) => {
    const gridApiRef = useRef<GridApi>(null);

    const onGridReady = (e: GridReadyEvent) => {
        gridApiRef.current = e.api;
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    let columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R#',
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 50,
            sortable: false
        },
        {
            colId: 'donorCenterAlias',
            field: 'donorCenterAlias',
            headerName: 'Donor Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 130,
            sortable: true
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Alias</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true
        },
        {
            colId: 'scheduledDonationDate',
            field: 'scheduledDonationDate',
            headerName: 'Donation Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Scheduled Donation<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 130,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'bloodDrawDate',
            field: 'bloodDrawDate',
            headerName: 'Blood Draw Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Blood Draw<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 120,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'resultsDate',
            field: 'resultsDate',
            headerName: 'Results Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Results<br />Date</SortableHeaderComponent>,
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 110,
            sortable: true,
            comparator: Utility.dateComparator
        },
        {
            colId: 'viewResults',
            headerName: '',
            headerClass: 'list-header',
            cellClass: 'list-cell',
            width: 125,
            sortable: false,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISerologyBloodDraw;

                return <Button bsSize='xsmall' disabled={!data.hasResults} onClick={() => props.onViewResults(data)}>View Results</Button>;
            }
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={45}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No blood draw records found...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onGridSizeChanged={onGridSizeChanged}
                onSortChanged={onSortChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;
